import { ActionTypes, SmartCarnetValidation } from '../constants/AppConstants';
import AppDispatcher from '../dispatcher/AppDispatcher';
import SmartCarnetService from '../services/SmartCarnetService';
import _ from 'lodash';

import OrderStore from '../stores/OrderStore';
import UserStore from '../stores/UserStore';
import SmartCarnetRecordSearch from '../records/smartCarnet/SmartCarnetRecordSearch';

const SmartCarnetActions = {
    async loadFromParams(params) {
        const search = await SmartCarnetRecordSearch.deserialize(params);

        return search;
    },

    async getFares({ fare, fares, products, isReturn = false, search }) {
        const user = UserStore.getCurrent();

        AppDispatcher.dispatch({
            actionType: ActionTypes.SMART_CARNET_PASSES_STARTED,
            ticketCode: fare.ticketType.code,
            isReturn,
        });

        if (user && user.id) {
            const isValid = await SmartCarnetActions.validate({
                destination: isReturn ? fare.origin : fare.destination,
                origin: isReturn ? fare.destination : fare.origin,
                isReturn,
                products,
            });

            if (!isValid) {
                AppDispatcher.dispatch({
                    actionType: ActionTypes.SMART_CARNET_PASSES_FAILED,
                    limitError: {
                        fare,
                        products,
                    },
                    isReturn,
                });
            }
        }

        try {
            let newFares;
            let newFare;
            if (
                fares.filter(
                    f => f.ticketType.code === fare.ticketType.code && f.tickets.size === products,
                ).size > 0
            ) {
                // If a fare's ticket type has been already fetched do not fetch it again
                newFares = fares;
                newFare = newFares.filter(f => f.ticketType.code === fare.ticketType.code).get(0);

                AppDispatcher.dispatch({
                    actionType: ActionTypes.SMART_CARNET_PASSES_FINISHED,
                });
            } else {
                newFares = isReturn
                    ? await SmartCarnetService.searchReturn({ search, products })
                    : await SmartCarnetService.search({ search, products });

                AppDispatcher.dispatch({
                    actionType: ActionTypes.SMART_CARNET_PASSES_FINISHED,
                });

                newFare = newFares.filter(f => f.ticketType.code === fare.ticketType.code).get(0);
                const replacedFares = fares.set(
                    fares.findIndex(f => f.ticketType.code === fare.ticketType.code),
                    newFare,
                );

                AppDispatcher.dispatch({
                    actionType: isReturn
                        ? ActionTypes.SMART_CARNET_RETURN_SEARCH_FINISHED
                        : ActionTypes.SMART_CARNET_SEARCH_FINISHED,
                    fares: replacedFares,
                });
            }

            if (!isReturn) {
                SmartCarnetActions.setSelectedFare(
                    newFare,
                    products * SmartCarnetValidation.PASSES_PER_BOOK,
                );
            } else {
                return newFare;
            }
        } catch (e) {
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMART_CARNET_PASSES_FAILED,
                error: e,
            });

            throw e;
        }

        return null;
    },

    async search({ search }) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMART_CARNET_SEARCH_STARTED,
            search,
        });

        SmartCarnetActions.setSelectedFare(null);

        try {
            const result = await SmartCarnetService.search({ search });

            AppDispatcher.dispatch({
                actionType: ActionTypes.SMART_CARNET_SEARCH_FINISHED,
                fares: result,
            });

            return {
                result,
                search,
            };
        } catch (e) {
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMART_CARNET_SEARCH_FAILED,
                error: e,
                search
            });
        }

        return null;
    },

    async searchReturn({ search }) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMART_CARNET_RETURN_SEARCH_STARTED,
            search,
        });

        try {
            const result = await SmartCarnetService.searchReturn({ search });

            AppDispatcher.dispatch({
                actionType: ActionTypes.SMART_CARNET_RETURN_SEARCH_FINISHED,
                fares: result,
            });

            return {
                result,
                search,
            };
        } catch (e) {
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMART_CARNET_RETURN_SEARCH_FAILED,
                error: e,
            });
        }

        return null;
    },

    setSelectedFare(fare, numberOfPasses) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMART_CARNET_SEARCH_SELECTED,
            fare,
            numberOfPasses,
        });
    },

    setSelectedFareReturn(fare, numberOfPasses = 1) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMART_CARNET_RETURN_SEARCH_SELECTED,
            fare,
            numberOfPasses,
        });
    },

    resetReturnModal() {
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMART_CARNET_RETURN_CLOSED,
        });
    },

    updateFilter({ filterType, value }) {
        _.defer(() => {
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMART_CARNET_FILTER_CHANGED,
                filterType,
                value,
            });
        });
    },

    async validate({ destination, isReturn = false, origin, products }) {
        const order = OrderStore.getCurrent();
        const user = UserStore.getCurrent();

        if (!user) {
            return true;
        }

        const originQuery = isReturn ? destination : origin;
        const destinationQuery = isReturn ? origin : destination;

        // eslint-disable-next-line no-shadow
        const getPassesFromOrder = ({ origin, destination, order }) => {
            const matchingFares = order
                .getSmartCarnetTrips()
                .map(trip => trip.getIn(['fares', 'outwardSingle']))
                .filter(
                    fare =>
                        fare.origin.nlc === origin.nlc && fare.destination.nlc === destination.nlc,
                );

            return matchingFares.reduce(
                (acc, cur) => (acc += cur.tickets.size * SmartCarnetValidation.PASSES_PER_BOOK),
                0,
            );
        };

        const existingOrderProducts = getPassesFromOrder({
            origin: originQuery,
            destination: destinationQuery,
            order,
        });

        try {
            await SmartCarnetService.validatePasses({
                newTickets:
                    existingOrderProducts + products * SmartCarnetValidation.PASSES_PER_BOOK,
                destination: destinationQuery,
                origin: originQuery,
                user,
            });
            return true;
        } catch (e) {
            if (e.body && e.body.status === 404) {
                return true;
            }
            return false;
        }
    },
    async getSmartCarnetTicketDetails(ticketID) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMART_CARNET_TICKET_DETAILS_STARTED,
            ticketID,
        });

        try {
            const result = await SmartCarnetService.getSingleTicket(ticketID);

            AppDispatcher.dispatch({
                actionType: ActionTypes.SMART_CARNET_TICKET_DETAILS_FINISHED,
                ticketID,
            });

            return result;
        } catch (e) {
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMART_CARNET_TICKET_DETAILS_FAILED,
                error: e,
            });
        }

        return null;
    },

    async getTickets(ticketStatus) {
        const user = UserStore.getCurrent();

        AppDispatcher.dispatch({
            actionType: ActionTypes.SMART_CARNET_TICKETS_STARTED,
        });

        try {
            const tickets = await SmartCarnetService.getTickets({ ticketStatus, user });

            AppDispatcher.dispatch({
                actionType: ActionTypes.SMART_CARNET_TICKETS_FINISHED,
                tickets,
            });
        } catch (error) {
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMART_CARNET_TICKETS_FAILED,
                error,
            });

            throw error;
        }
    },
};

export default SmartCarnetActions;



// WEBPACK FOOTER //
// ./src/actions/SmartCarnetActions.js